import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentWorkspaceUrlPrefix } from '@tinct/core-workspace';

/**
 * Where the studio's deep links go. The studio is hosted by two apps: the
 * customer app (workspace-prefixed routes, funnel or management studio) and
 * the back office (admin routes, no funnel, no variant preview page). The
 * host decides; the studio only reads.
 */
export type StudioHostUrls = {
  /** Back to the campaign (back arrow, "run in background"). */
  campaignUrl: string;
  /** This studio's own URL — base for the background job's completed link. */
  studioUrl: string;
  /** "Review" from the changes-not-live notice: the studio on its variants tab. */
  reviewVariantsUrl: string;
  /** Publish flow. `null` hides every publish action (the host has no funnel). */
  publishUrl: string | null;
  /** Per-variant internal preview. `null` hides the Preview action. */
  variantPreviewUrl: ((pageId: string) => string) | null;
};

const StudioHostContext = createContext<StudioHostUrls | null>(null);

export const StudioHostProvider = StudioHostContext.Provider;

/**
 * The host's URLs, defaulting to the customer app's workspace-prefixed routes
 * so that app needs no provider: inside the funnel the studio is the preview
 * step (and leaves to the dashboard), everywhere else it is the management
 * studio (and leaves to the campaign).
 */
export function useStudioHostUrls(campaignId: string): StudioHostUrls {
  const host = useContext(StudioHostContext);
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();
  const { pathname } = useLocation();
  if (host) return host;

  const inFunnel = pathname.includes('/funnel-campaigns/');
  return {
    campaignUrl: inFunnel ? `${workspaceUrl}/dashboard` : `${workspaceUrl}/campaigns/${campaignId}`,
    studioUrl: inFunnel
      ? `${workspaceUrl}/funnel-campaigns/${campaignId}/preview`
      : `${workspaceUrl}/campaigns/${campaignId}/studio`,
    reviewVariantsUrl: `${workspaceUrl}/funnel-campaigns/${campaignId}/preview?view=variants`,
    publishUrl: `${workspaceUrl}/funnel-campaigns/${campaignId}/publish`,
    variantPreviewUrl: (pageId) =>
      `${workspaceUrl}/campaigns/${campaignId}/landing-pages/${pageId}/preview`,
  };
}
